import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 420;

/**
 * Header navigation menu controller.
 *
 * Keeps the first-level dropdown open while the pointer moves between:
 * - the first-level menu tab
 * - the second-level dropdown
 * - an expanded third-level list
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  #state = {
    activeItem: null,
  };

  #pointerX = 0;
  #pointerY = 0;

  connectedCallback() {
    super.connectedCallback();

    const signal = this.#abortController.signal;

    this.addEventListener('pointerenter', this.#cancelDeactivation, {
      signal,
    });

    this.addEventListener('pointerleave', this.#handleBoundaryLeave, {
      signal,
    });

    this.addEventListener('focusin', this.#cancelDeactivation, {
      signal,
    });

    this.addEventListener('focusout', this.#handleFocusOut, {
      signal,
    });

    document.addEventListener('pointermove', this.#trackPointer, {
      signal,
      passive: true,
    });

    this.overflowMenu?.addEventListener(
      'pointerenter',
      this.#cancelDeactivation,
      {
        signal,
      }
    );

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#handleBoundaryLeave,
      {
        signal,
      }
    );

    /*
     * The submenu references can sit outside the overflow-list shadow
     * boundary. Listen to each submenu directly so entering a dropdown
     * always cancels its pending closure.
     */
    this.#getSubmenus().forEach((submenu) => {
      submenu.addEventListener(
        'pointerenter',
        this.#cancelDeactivation,
        { signal }
      );

      submenu.addEventListener(
        'pointerleave',
        this.#handleBoundaryLeave,
        { signal }
      );

      submenu.addEventListener(
        'focusin',
        this.#cancelDeactivation,
        { signal }
      );

      submenu.addEventListener(
        'focusout',
        this.#handleFocusOut,
        { signal }
      );
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
  }

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      ) ?? null
    );
  }

  get overflowHovered() {
    return this.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate a first-level menu item.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const menuListItem = item.closest('.menu-list__list-item');
    const isOverflowItem =
      menuListItem?.getAttribute('slot') === 'overflow';

    this.dataset.overflowExpanded = isOverflowItem
      ? 'true'
      : 'false';

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const overflowMenuHeight =
      this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && isOverflowItem) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu
      ? Math.max(submenu.scrollHeight, submenu.offsetHeight, overflowMenuHeight)
      : 0;

    this.style.setProperty(
      '--submenu-height',
      `${submenuHeight}px`
    );

    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(
    this.#activateHandler,
    ACTIVATE_DELAY
  );

  /**
   * Called by the Liquid event bindings.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (
      item === this.#state.activeItem ||
      this.#state.activeItem
    ) {
      this.#debouncedDeactivate();
    }
  }

  #cancelDeactivation = () => {
    this.#debouncedDeactivate.cancel();
  };

  #handleBoundaryLeave = (event) => {
    const destination = event.relatedTarget;

    if (
      destination instanceof Node &&
      this.#isNodeInsideActiveRegion(destination)
    ) {
      this.#debouncedDeactivate.cancel();
      return;
    }

    this.#debouncedDeactivate();
  };

  #handleFocusOut = (event) => {
    const destination = event.relatedTarget;

    if (
      destination instanceof Node &&
      this.#isNodeInsideActiveRegion(destination)
    ) {
      return;
    }

    this.#debouncedDeactivate();
  };

  #trackPointer = (event) => {
    this.#pointerX = event.clientX;
    this.#pointerY = event.clientY;

    if (!this.#state.activeItem) return;

    /*
     * The pointer can cross a small DOM or shadow-root boundary when
     * moving between the second and third menu levels. Check the actual
     * element beneath the pointer rather than relying only on :hover.
     */
    if (this.#isPointerInsideActiveRegion()) {
      this.#debouncedDeactivate.cancel();
    }
  };

  #getSubmenus() {
    const submenuRefs = this.refs.submenu;

    if (Array.isArray(submenuRefs)) {
      return submenuRefs.filter(
        (submenu) => submenu instanceof HTMLElement
      );
    }

    if (submenuRefs instanceof HTMLElement) {
      return [submenuRefs];
    }

    return Array.from(
      this.querySelectorAll('[ref="submenu[]"]')
    ).filter((submenu) => submenu instanceof HTMLElement);
  }

  #isPointerInsideActiveRegion() {
    const pointerTarget = document.elementFromPoint(
      this.#pointerX,
      this.#pointerY
    );

    return (
      pointerTarget instanceof Node &&
      this.#isNodeInsideActiveRegion(pointerTarget)
    );
  }

  #isNodeInsideActiveRegion(node) {
    const activeItem = this.#state.activeItem;

    if (!activeItem) return false;

    const activeListItem = activeItem.closest(
      '.menu-list__list-item'
    );

    const activeSubmenu = findSubmenu(activeItem);

    if (activeListItem?.contains(node)) {
      return true;
    }

    if (activeSubmenu?.contains(node)) {
      return true;
    }

    if (this.overflowMenu?.contains(node)) {
      return true;
    }

    /*
     * Covers slotted and shadow-root content whose visual submenu is
     * still part of the active header navigation.
     */
    return this.#getSubmenus().some((submenu) =>
      submenu.contains(node)
    );
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    /*
     * This is the critical safeguard. Recheck the pointer and focus at
     * the moment the delayed close executes.
     */
    if (
      this.#isPointerInsideActiveRegion() ||
      this.#isNodeInsideActiveRegion(document.activeElement) ||
      this.matches(':focus-within') ||
      this.overflowHovered
    ) {
      return;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    window.setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(
    this.#deactivate,
    DEACTIVATE_DELAY
  );

  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Locate the first-level menu anchor associated with an event target.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const menuListItem = element.closest(
    '.menu-list__list-item'
  );

  if (menuListItem?.matches('[slot="more"]')) {
    const overflowItem =
      menuListItem.parentElement?.querySelector(
        '[slot="overflow"]'
      );

    return findMenuItem(overflowItem);
  }

  const menuItem =
    menuListItem?.querySelector(':scope > [ref="menuitem"]') ??
    element.closest('[ref="menuitem"]');

  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Locate the submenu belonging to a first-level menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  const menuListItem = element.closest(
    '.menu-list__list-item'
  );

  const submenu =
    menuListItem?.querySelector(
      ':scope > [ref="submenu[]"]'
    ) ??
    element.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement ? submenu : null;
}